import React, { useMemo, useCallback } from "react";
import type { ColumnDef, CellContext } from "@tanstack/react-table";
import { FileExcelIcon, FileIcon, FolderIcon, PdfIcon, RowActions } from "shared-portal-component";

import Table from "@/components/atomic/Table/Table";
import FileUploadProgress from "@/components/atomic/FileUploadProgress/FileUploadProgress";
import CloneStatusIndicator from "@/components/atomic/CloneStatusIndicator/CloneStatusIndicator";
import type { FileMetadata } from "@/types/poc/helia";
import type { UploadProgress } from "@/store/slices/poc/heliaSlice";
import { useCloningProgress } from "@/hooks/p2p/useCloningProgress";
import { useFileCloneStatus, useFileCloneError } from "@/hooks/p2p/useFileCloneStatus";
import { formatFileSize } from "@/utils/formatters.util";

// Magic ID for parent navigation row
const PARENT_ROW_ID = '__parent__';
const MAX_RETRY_COUNT = 3;

// Subscribes to clone status, error, and progress via hooks so the columns
// useMemo doesn't depend on any clone state (which would unmount RowActions
// on every status change, closing the action menu).
const ConnectedCloneStatus: React.FC<{
  cid: string;
  file: FileMetadata;
}> = ({ cid, file }) => {
  const status = useFileCloneStatus(cid, file);
  const error = useFileCloneError(cid);
  const progress = useCloningProgress(cid);
  return <CloneStatusIndicator status={status} error={error} progress={progress} />;
};

// Helper to get the icon
const getFileIcon = (type: string) => {
  switch (type) {
    case "folder":
      return <FolderIcon size="2x" />;
    case "docx":
      return <FileIcon size="2x" />;
    case "pdf":
      return <PdfIcon size="2x" />;
    case "xlsx":
      return <FileExcelIcon size="2x" />;
    case "pptx":
      return <FileIcon size="2x" />;
    default:
      return null;
  }
};

interface ActionsCellProps {
  row: FileMetadata;
  handleDownload: (row: FileMetadata) => void;
  handleDeleteFile: (row: FileMetadata) => void;
  handleDeleteFolder: (row: FileMetadata) => void;
  canDeleteFile: (row: FileMetadata) => boolean;
  canDeleteFolder: (row: FileMetadata) => boolean;
}

const ActionsCell: React.FC<ActionsCellProps> = ({
  row,
  handleDownload,
  handleDeleteFile,
  handleDeleteFolder,
  canDeleteFile,
  canDeleteFolder,
}) => {
  const isFolder = row.type === 'folder';
  const isParentRow = row.id === PARENT_ROW_ID;
  const cloneStatus = useFileCloneStatus(row.cid, row);

  // Build actions array - must be before any conditional returns to satisfy Rules of Hooks
  const actions = useMemo(
    () => {
      interface ActionItem { label: string; onClick: () => void }

      // No actions for parent navigation row
      if (isParentRow) {
        return [] as ActionItem[];
      }
      // Folder actions
      if (isFolder) {
        const folderActions: ActionItem[] = [];
        if (canDeleteFolder(row)) {
          folderActions.push({ label: 'Delete Folder', onClick: () => { handleDeleteFolder(row); } });
        }
        return folderActions;
      }
      // File actions - no download for files without decryption access
      const fileActions: ActionItem[] = [];
      if (cloneStatus !== 'no-access') {
        const downloadLabel = cloneStatus === 'cloned' ? 'Download File' : 'Clone and download';
        fileActions.push({ label: downloadLabel, onClick: () => { handleDownload(row); } });
      }
      if (canDeleteFile(row)) {
        fileActions.push({ label: 'Delete', onClick: () => { handleDeleteFile(row); } });
      }
      return fileActions;
    },
    [row, isFolder, isParentRow, cloneStatus, handleDownload, handleDeleteFile, handleDeleteFolder, canDeleteFile, canDeleteFolder]
  );

  if (actions.length === 0) {
    return null;
  }

  return <RowActions actions={actions} />;
};

interface SixTableProps {
  data: FileMetadata[];
  isAtRoot: boolean;
  onFolderClick: (folder: FileMetadata) => void;
  onNavigateUp: () => void;
  handleDownload: (row: FileMetadata) => void;
  handleDeleteFile: (row: FileMetadata) => void;
  handleDeleteFolder: (row: FileMetadata) => void;
  canDeleteFile: (row: FileMetadata) => boolean;
  canDeleteFolder: (row: FileMetadata) => boolean;
  uploadProgress?: Record<string, UploadProgress>;
  onProgressComplete?: (fileName: string) => void;
  onRetry?: (fileName: string) => void;
}

const SixTable: React.FC<SixTableProps> = ({
  data = [],
  isAtRoot,
  onFolderClick,
  onNavigateUp,
  handleDownload,
  handleDeleteFile,
  handleDeleteFolder,
  canDeleteFile,
  canDeleteFolder,
  uploadProgress = {},
  onProgressComplete,
  onRetry
}) => {
  // Create ".." navigation row when not at root
  const parentRow: FileMetadata | null = !isAtRoot ? {
    id: PARENT_ROW_ID,
    cid: '',
    name: '..',
    size: 0,
    type: 'folder',
    sixId: '',
    addedBy: '',
    lastModified: 0,
    path: '',
    uploadedByUserId: '',
    uploadedByUserName: '',
    decryptionCIDS: [],
  } : null;

  // Handle click on a row - for folders and ".." navigation
  const handleRowClick = useCallback((row: FileMetadata) => {
    if (row.id === PARENT_ROW_ID) {
      onNavigateUp();
    } else if (row.type === 'folder') {
      onFolderClick(row);
    }
  }, [onNavigateUp, onFolderClick]);

  const columns: Array<ColumnDef<FileMetadata>> = useMemo(() => [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row: { original } }: CellContext<FileMetadata, unknown>) => {
        const isFolder = original.type === 'folder';
        const isParentRow = original.id === PARENT_ROW_ID;
        const isClickable = isFolder || isParentRow;
        const showCloneStatus = !isFolder && !isParentRow && original.cid !== '';

        const handleClick = isClickable
          ? () => { handleRowClick(original); }
          : undefined;

        return (
          <span
            className={`flex items-center gap-2 ${isClickable ? 'cursor-pointer hover:underline' : ''}`}
            onClick={handleClick}
          >
            {getFileIcon(original.type)}
            <span className="font-semibold text-[#130151]">{original.name}</span>
            {showCloneStatus && (
              <ConnectedCloneStatus
                cid={original.cid}
                file={original}
              />
            )}
          </span>
        );
      },
    },
    {
      accessorKey: "uploadedByUserName",
      header: "Added by",
      cell: (info: CellContext<FileMetadata, unknown>) => {
        const isParentRow = info.row.original.id === PARENT_ROW_ID;
        if (isParentRow) {
          return <span className="text-[#130151]">-</span>;
        }
        return <span className="text-[#130151]">{info.getValue<string>()}</span>;
      },
    },
    {
      accessorKey: "lastModified",
      header: "Added on",
      cell: (info: CellContext<FileMetadata, unknown>) => {
        const isParentRow = info.row.original.id === PARENT_ROW_ID;
        if (isParentRow) {
          return <span className="text-[#130151]">-</span>;
        }
        return <span className="text-[#130151]">{info.getValue<string>()}</span>;
      },
    },
    {
      accessorKey: "size",
      header: "Size",
      cell: (info: CellContext<FileMetadata, unknown>) => {
        const { type, id } = info.row.original;
        // Show "-" for folders and parent navigation row
        if (type === 'folder' || id === PARENT_ROW_ID) {
          return <span className="text-[#130151]">-</span>;
        }
        return <span className="text-[#130151]">{formatFileSize(info.getValue<number>())}</span>;
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <ActionsCell
          row={row.original}
          handleDownload={handleDownload}
          handleDeleteFile={handleDeleteFile}
          handleDeleteFolder={handleDeleteFolder}
          canDeleteFile={canDeleteFile}
          canDeleteFolder={canDeleteFolder}
        />
      ),
      enableSorting: false,
    },
  ], [handleDownload, handleDeleteFile, handleDeleteFolder, handleRowClick, canDeleteFile, canDeleteFolder]);

  // Filter out files that are currently being uploaded
  const filesNotUploading = useMemo(() => {
    const uploadingFileNames = new Set(Object.keys(uploadProgress));
    return data.filter(file => !uploadingFileNames.has(file.name));
  }, [data, uploadProgress]);

  // Combine parent row with data
  const tableData = parentRow !== null
    ? [parentRow, ...filesNotUploading]
    : filesNotUploading;

  return (
    <div>
      {/* Show upload progress bars for files being uploaded - these replace the file rows */}
      {Object.entries(uploadProgress).map(([fileName, progress]) => {
        const retriesExhausted = (progress.retryCount ?? 0) >= MAX_RETRY_COUNT;
        return (
          <FileUploadProgress
            key={fileName}
            fileName={progress.fileName}
            progress={progress.progress}
            stage={progress.stage}
            error={progress.error}
            onComplete={onProgressComplete}
            onRetry={retriesExhausted ? undefined : onRetry}
          />
        );
      })}
      {/* Show table with files that are not currently uploading */}
      <Table data={tableData} columns={columns} />
    </div>
  );
}

export default SixTable;
